import type { FilterState } from './types';
import type { SearchRequest } from '../../services/ragSearchService';
import { pruneHiddenFilters } from '../../config/searchFilterFields';
import { dropOpenEndedMaxes } from '../../config/searchPresets';

/** The /search sort menu values (the URL `sort` param). */
export type SortKey = 'price-asc' | 'price-desc' | 'date-newest' | 'date-oldest';

/**
 * Build the shared /api/search request (filters + sort) from the current filter
 * state. Pagination, AI mode, and the auth token are layered on by each caller —
 * the paginated grid fetch vs the full-set map fetch — so the two never drift.
 *
 * Values the filters panel hides for the chosen type / category / listing type
 * (bedrooms on land, a pool on an office, pet-friendly for sale …) are pruned
 * first, so they can never reach the API — not even from an old or hand-edited
 * deep link such as `?type=LAND&bedrooms=3` (config/searchFilterFields.ts).
 */
export const buildSearchRequestBase = (
  rawFilters: FilterState,
  sortBy: SortKey,
  lang: 'en' | 'ar',
): SearchRequest => {
  // Hidden values never leave, and a «إلى 5+» (open-ended top) is no upper limit.
  const filters = dropOpenEndedMaxes(pruneHiddenFilters(rawFilters));
  const rentPriceSet = filters.listingType === 'rent' && Boolean(filters.minPrice || filters.maxPrice);
  const listingTypeMap: Record<string, string> = { rent: 'RENT', buy: 'SALE' };
  const sortMap: Record<SortKey, 'relevance' | 'price-asc' | 'price-desc' | 'newest'> = {
    'date-newest': 'newest',
    'date-oldest': 'newest', // backend has no "oldest"; sorted locally
    'price-asc': 'price-asc',
    'price-desc': 'price-desc',
  };
  // District radius mode: filter by server-side haversine around the centroid
  // instead of the strict areaId/cityId so results can extend just past the border.
  const locRadiusActive = !!(
    filters.locationRadiusKm && filters.locationLat != null && filters.locationLng != null
  );
  const hasTextQuery = !!(filters.query && filters.query.trim());
  return {
    q: filters.query || undefined,
    cityId: !locRadiusActive ? (filters.cityId || undefined) : undefined,
    areaId: !locRadiusActive ? (filters.areaId || undefined) : undefined,
    city: (!locRadiusActive && !filters.cityId) ? (filters.cityName || undefined) : undefined,
    lat: locRadiusActive ? (filters.locationLat ?? undefined) : undefined,
    lng: locRadiusActive ? (filters.locationLng ?? undefined) : undefined,
    radius: locRadiusActive ? (filters.locationRadiusKm ?? undefined) : undefined,
    propertyType: filters.propertyType || undefined,
    // A specific propertyType wins over the macro-category (backend also enforces
    // this precedence); only send category when no explicit type is chosen.
    category: (filters.category && !filters.propertyType) ? filters.category : undefined,
    listingType: filters.listingType !== 'all' ? listingTypeMap[filters.listingType] : undefined,
    minPrice: filters.minPrice ? parseInt(filters.minPrice) : undefined,
    maxPrice: filters.maxPrice ? parseInt(filters.maxPrice) : undefined,
    pricePeriod: rentPriceSet ? filters.pricePeriod : undefined,
    bedrooms: filters.bedrooms ? parseInt(filters.bedrooms) : undefined,
    bathrooms: filters.bathrooms ? parseInt(filters.bathrooms) : undefined,
    bedroomsMax: filters.bedroomsMax ? parseInt(filters.bedroomsMax) : undefined,
    bathroomsMax: filters.bathroomsMax ? parseInt(filters.bathroomsMax) : undefined,
    minArea: filters.minArea ? parseInt(filters.minArea) : undefined,
    maxArea: filters.maxArea ? parseInt(filters.maxArea) : undefined,
    furnished: filters.furnished || undefined,
    petFriendly: filters.petFriendly || undefined,
    parking: filters.parking || undefined,
    swimmingPool: filters.swimmingPool || undefined,
    gym: filters.gym || undefined,
    garden: filters.garden || undefined,
    elevator: filters.elevator || undefined,
    security: filters.security || undefined,
    sort: sortMap[sortBy] || 'relevance',
    // Only send `lang` when the user actually typed a query — for default
    // browsing the backend returns all properties regardless of UI language.
    ...(hasTextQuery ? { lang } : {}),
  };
};
